import { useEffect, useState } from 'react'
import { read, utils as xlsxUtils } from 'xlsx'

import type { RowValidated } from '../../../types'
import { sanitizeRows } from '../../../utils'

export const useFileContents = (file: File | null) => {
  /**
   * Error occurred during the file reading process.
   */
  const [hasError, setError] = useState(false)
  /**
   * Number of invalid rows in the file.
   */
  const [nInvalidRows, setNInvalidRows] = useState(0)
  /**
   * Rows extracted from the file.
   */
  const [rows, setRows] = useState<RowValidated[]>([])

  useEffect(() => {
    setError(false)
    setNInvalidRows(0)
    setRows([])
  }, [file])

  useEffect(() => {
    if (!file) {
      setError(false)
      setNInvalidRows(0)
      setRows([])
      return
    }

    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const result = e.target?.result
        const workbook = read(result, { type: 'binary' })
        const sheetName = workbook.SheetNames[0]
        const sheet = workbook.Sheets[sheetName]

        if (sheet) {
          const json: Record<string, string>[] = xlsxUtils.sheet_to_json(sheet)
          const { nInvalidRows, sanitizedRows } = sanitizeRows(json)
          setError(nInvalidRows === sanitizedRows.length)
          setRows(sanitizedRows)
          setNInvalidRows(nInvalidRows)
        }
      } catch (err) {
        setError(true)
      }
    }
    reader.onerror = () => {
      setError(true)
    }

    reader.readAsArrayBuffer(file)
  }, [file])

  return {
    hasError,
    rows,
    nInvalidRows,
  }
}
